import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Bug, MessageSquare, Send, CheckCircle, Clock, AlertCircle,
  ChevronRight, Plus, Menu, X, Filter, Loader2, Mail, Phone, MapPin,
  HelpCircle, BookOpen, FileText, Headphones, ExternalLink, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const LOGO_URL = "/logo.png";

const API_URL = (() => {
  const isProductionDomain = typeof window !== 'undefined' && 
    window.location.hostname !== 'localhost' && 
    !window.location.hostname.includes('preview.emergentagent.com');
  return isProductionDomain ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');
})();

// Informations de contact ResaWork
const CONTACT_INFO = {
  email: 'support@resawork.fr',
  phone: '04 84 49 27 00',
  address: '25 Av du Tubé, 13800 ISTRES',
  hours: 'Lun-Ven : 9h-18h'
};

// FAQ détaillée
const faqCategories = [
  {
    title: 'Premiers pas',
    icon: BookOpen,
    questions: [
      {
        q: 'Comment créer mon compte entreprise ?',
        a: 'Cliquez sur "Démarrer gratuitement" sur la page d\'accueil. Remplissez vos informations et vous recevrez un email de confirmation avec vos identifiants de connexion.'
      },
      {
        q: 'Comment fonctionne la période d\'essai ?',
        a: 'La période d\'essai dure 15 jours et vous donne accès à toutes les fonctionnalités avec une limite de 3 clients. Aucune carte bancaire n\'est requise.'
      },
      {
        q: 'Comment intégrer le widget sur mon site ?',
        a: 'Depuis votre espace entreprise, allez dans Widget > Mes Widgets. Créez ou sélectionnez un widget, puis copiez le code iframe fourni et collez-le dans votre site (WordPress, Wix, ou tout autre CMS).'
      }
    ]
  },
  {
    title: 'Gestion des services',
    icon: FileText,
    questions: [
      {
        q: 'Comment ajouter un nouveau service ?',
        a: 'Rendez-vous dans Mon activité > Services, puis cliquez sur "Ajouter un service". Renseignez le nom, la description, la durée et choisissez le mode de tarification (taux horaire, prix fixe ou informatif).'
      },
      {
        q: 'Comment configurer le crédit d\'impôt ?',
        a: 'Lors de la création ou modification d\'un service, cochez "Éligible au crédit d\'impôt". Le prix après réduction (50%) sera automatiquement affiché à vos clients.'
      },
      {
        q: 'Puis-je modifier un service utilisé dans des réservations ?',
        a: 'Oui, vous pouvez modifier les informations d\'un service à tout moment. Cependant, les réservations existantes conserveront les informations au moment de leur création.'
      }
    ]
  },
  {
    title: 'Réservations & Planning',
    icon: HelpCircle,
    questions: [
      {
        q: 'Comment valider une demande d\'abonnement ?',
        a: 'Les demandes d\'abonnement apparaissent dans Réservations > Demandes Abo. Cliquez sur une demande pour voir les détails, puis acceptez ou refusez. Le client sera notifié automatiquement par email.'
      },
      {
        q: 'Comment créer une réservation manuelle ?',
        a: 'Dans le Planning, cliquez sur un créneau vide puis sélectionnez "Créer une réservation". Choisissez un client existant ou créez-en un nouveau, puis remplissez les détails de l\'intervention.'
      },
      {
        q: 'Comment exporter mon planning en PDF ?',
        a: 'Dans la vue Planning, cliquez sur l\'icône de téléchargement en haut à droite. Le planning de la semaine sera exporté en PDF avec votre logo et les détails des interventions.'
      }
    ]
  },
  {
    title: 'Abonnement & Facturation',
    icon: Headphones,
    questions: [
      {
        q: 'Comment passer d\'un essai gratuit à un abonnement payant ?',
        a: 'Rendez-vous dans Mon Compte > Ma Licence, puis cliquez sur "Mettre à niveau". Choisissez entre l\'abonnement mensuel ou annuel et procédez au paiement sécurisé par carte.'
      },
      {
        q: 'Où trouver mes factures ResaWork ?',
        a: 'Vos factures sont disponibles dans Mon Compte > Mes Factures. Vous pouvez les consulter et les télécharger en PDF à tout moment.'
      },
      {
        q: 'Comment annuler mon abonnement ?',
        a: 'Contactez notre support à support@resawork.fr pour demander l\'annulation. Votre abonnement restera actif jusqu\'à la fin de la période en cours.'
      }
    ]
  }
];

const getStatusBadge = (status) => {
  switch (status) {
    case 'open': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" />Ouvert</Badge>;
    case 'in_progress': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><AlertCircle className="w-3 h-3 mr-1" />En cours</Badge>;
    case 'resolved': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Résolu</Badge>;
    default: return <Badge variant="secondary">{status}</Badge>;
  }
};

const getPriorityBadge = (priority) => {
  switch (priority) {
    case 'high': return <Badge variant="destructive">Haute</Badge>;
    case 'medium': return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Moyenne</Badge>;
    case 'low': return <Badge variant="secondary">Basse</Badge>;
    default: return <Badge variant="secondary">{priority}</Badge>;
  }
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('faq');
  
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    type: 'bug',
    priority: 'medium',
    email: '',
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets`);
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newTicket.title || !newTicket.description || !newTicket.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTicket),
      });

      if (response.ok) {
        toast.success('Ticket créé avec succès !');
        setIsDialogOpen(false);
        setNewTicket({ title: '', description: '', type: 'bug', priority: 'medium', email: '' });
        fetchTickets();
      } else {
        toast.error('Erreur lors de la création du ticket');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTickets = filter === 'all' 
    ? tickets 
    : tickets.filter(t => t.status === filter);

  // Filtrer les FAQ par recherche
  const filteredFAQ = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ResaWork" className="h-16" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/docs" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Documentation</Link>
              <Link to="/changelog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Changelog</Link>
              <Link to="/tickets" className="text-sm font-medium text-primary">Support</Link>
            </nav>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">Connexion</Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/onboarding">Démarrer</Link>
              </Button>
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background pt-16 md:hidden">
          <nav className="flex flex-col p-4 gap-4">
            <Link to="/docs" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Documentation</Link>
            <Link to="/changelog" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Changelog</Link>
            <Link to="/tickets" className="text-lg font-medium text-primary" onClick={() => setMobileMenuOpen(false)}>Support</Link>
            <hr className="my-2" />
            <Link to="/login" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Connexion</Link>
            <Button asChild className="rounded-full mt-2">
              <Link to="/onboarding" onClick={() => setMobileMenuOpen(false)}>Démarrer</Link>
            </Button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="py-12 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4">
                <Headphones className="w-3.5 h-3.5 mr-1.5" />
                Centre d'aide
              </Badge>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
                Comment pouvons-nous vous aider ?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Trouvez rapidement des réponses à vos questions ou contactez notre équipe support
              </p>
            </div>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto mb-8">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans l'aide..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full"
              />
            </div>

            {/* Quick Contact Cards */}
            <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <Card className="text-center p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = `mailto:${CONTACT_INFO.email}`}>
                <Mail className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold mb-1">Email</h3>
                <p className="text-sm text-muted-foreground">{CONTACT_INFO.email}</p>
              </Card>
              <Card className="text-center p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = `tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}>
                <Phone className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold mb-1">Téléphone</h3>
                <p className="text-sm text-muted-foreground">{CONTACT_INFO.phone}</p>
                <p className="text-xs text-muted-foreground mt-1">{CONTACT_INFO.hours}</p>
              </Card>
              <Card className="text-center p-6 hover:shadow-lg transition-shadow">
                <MapPin className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold mb-1">Adresse</h3>
                <p className="text-sm text-muted-foreground">{CONTACT_INFO.address}</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Tabs Content */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
                <TabsTrigger value="faq">FAQ</TabsTrigger>
                <TabsTrigger value="tickets">Mes tickets</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
              </TabsList>

              {/* FAQ Tab */}
              <TabsContent value="faq" className="space-y-8">
                {searchQuery && filteredFAQ.length === 0 ? (
                  <Card className="p-12 text-center">
                    <HelpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">Aucun résultat</h3>
                    <p className="text-muted-foreground mb-4">
                      Aucune question ne correspond à "{searchQuery}"
                    </p>
                    <Button onClick={() => { setSearchQuery(''); setActiveTab('tickets'); setIsDialogOpen(true); }} className="rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un ticket
                    </Button>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {(searchQuery ? filteredFAQ : faqCategories).map((category, idx) => (
                      <Card key={idx}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <category.icon className="w-5 h-5 text-primary" />
                            </div>
                            <CardTitle className="text-lg">{category.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {category.questions.map((item, qIdx) => (
                            <div key={qIdx} className="border-b border-border pb-4 last:border-0 last:pb-0">
                              <h4 className="font-medium mb-2 flex items-start gap-2">
                                <HelpCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                                {item.q}
                              </h4>
                              <p className="text-sm text-muted-foreground pl-6">{item.a}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Documentation Link */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <BookOpen className="w-10 h-10 text-primary" />
                      <div>
                        <h3 className="font-semibold">Consultez notre documentation complète</h3>
                        <p className="text-sm text-muted-foreground">
                          16 tutoriels détaillés pour maîtriser toutes les fonctionnalités
                        </p>
                      </div>
                    </div>
                    <Button asChild className="rounded-full">
                      <Link to="/docs">
                        Voir la documentation
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tickets Tab */}
              <TabsContent value="tickets" className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Vos tickets</h2>
                  <div className="flex items-center gap-3">
                    <Select value={filter} onValueChange={setFilter}>
                      <SelectTrigger className="w-40">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="open">Ouverts</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="resolved">Résolus</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full" data-testid="new-ticket-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Nouveau ticket
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Créer un ticket</DialogTitle>
                          <DialogDescription>
                            Décrivez votre problème ou votre demande. Notre équipe vous répondra sous 24h ouvrées.
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                              type="email"
                              placeholder="votre@email.fr"
                              value={newTicket.email}
                              onChange={(e) => setNewTicket({ ...newTicket, email: e.target.value })}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Type</Label>
                              <Select
                                value={newTicket.type}
                                onValueChange={(value) => setNewTicket({ ...newTicket, type: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="bug">Bug / Problème</SelectItem>
                                  <SelectItem value="feature">Suggestion</SelectItem>
                                  <SelectItem value="question">Question</SelectItem>
                                  <SelectItem value="billing">Facturation</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Priorité</Label>
                              <Select
                                value={newTicket.priority}
                                onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Basse</SelectItem>
                                  <SelectItem value="medium">Moyenne</SelectItem>
                                  <SelectItem value="high">Haute (urgent)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Sujet *</Label>
                            <Input
                              placeholder="Résumé de votre demande"
                              value={newTicket.title}
                              onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Description *</Label>
                            <Textarea
                              placeholder="Décrivez votre problème en détail. Incluez les étapes pour reproduire le bug si applicable..."
                              rows={5}
                              value={newTicket.description}
                              onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                            />
                          </div>
                          
                          <Button 
                            className="w-full rounded-full" 
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Envoyer le ticket
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucun ticket</h3>
                      <p className="text-muted-foreground mb-4">
                        {filter === 'all' 
                          ? "Vous n'avez pas encore créé de ticket"
                          : `Aucun ticket avec le statut "${filter}"`
                        }
                      </p>
                      <Button onClick={() => setIsDialogOpen(true)} className="rounded-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Créer mon premier ticket
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {filteredTickets.map((ticket) => (
                      <Card key={ticket.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-xs text-muted-foreground font-mono">
                                  #{ticket.id?.slice(0, 8)}
                                </span>
                                {getStatusBadge(ticket.status)}
                                {getPriorityBadge(ticket.priority)}
                                <Badge variant="outline">{ticket.type}</Badge>
                              </div>
                              <h3 className="font-medium mb-1">{ticket.title}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {ticket.description}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Créé le {new Date(ticket.created_at).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                                })}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-2" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Contact Tab */}
              <TabsContent value="contact" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Contactez-nous</CardTitle>
                      <CardDescription>
                        Notre équipe est disponible pour vous accompagner
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">Email</p>
                          <a href={`mailto:${CONTACT_INFO.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                            {CONTACT_INFO.email}
                          </a>
                          <p className="text-xs text-muted-foreground mt-1">Réponse sous 24h ouvrées</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">Téléphone</p>
                          <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="text-muted-foreground hover:text-primary transition-colors">
                            {CONTACT_INFO.phone}
                          </a>
                          <p className="text-xs text-muted-foreground mt-1">{CONTACT_INFO.hours}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">Adresse</p>
                          <p className="text-muted-foreground">{CONTACT_INFO.address}</p>
                          <p className="text-xs text-muted-foreground mt-1">France</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>À propos de ResaWork</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-muted-foreground">
                        ResaWork est une solution de gestion de réservations en ligne développée par 
                        <strong> Agence MSC - Mon Service Com</strong>, spécialisée dans le développement 
                        de solutions digitales pour les entreprises de services.
                      </p>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium mb-2">Siège social :</p>
                        <p className="text-sm text-muted-foreground">
                          Agence MSC - Mon Service Com<br />
                          52 rue des Pétouses<br />
                          13800 ISTRES
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <Button asChild variant="outline" className="rounded-full flex-1">
                          <Link to="/mentions-legales">Mentions légales</Link>
                        </Button>
                        <Button asChild variant="outline" className="rounded-full flex-1">
                          <Link to="/confidentialite">Confidentialité</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* CTA */}
                <Card className="bg-primary text-primary-foreground">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-2xl font-bold mb-2">Prêt à simplifier vos réservations ?</h3>
                    <p className="text-primary-foreground/80 mb-6">
                      Essayez ResaWork gratuitement pendant 15 jours, sans engagement
                    </p>
                    <Button asChild size="lg" variant="secondary" className="rounded-full">
                      <Link to="/onboarding">
                        Démarrer l'essai gratuit
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="ResaWork" className="h-12 mb-4 brightness-0 invert" />
              <p className="text-slate-400 text-sm">
                Solution de gestion de réservations en ligne pour les entreprises de services.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Produit</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/#fonctionnalites" className="hover:text-white transition-colors">Fonctionnalités</Link></li>
                <li><Link to="/#tarifs" className="hover:text-white transition-colors">Tarifs</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors">Documentation</Link></li>
                <li><Link to="/changelog" className="hover:text-white transition-colors">Changelog</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/tickets" className="hover:text-white transition-colors">Centre d'aide</Link></li>
                <li><a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white transition-colors">{CONTACT_INFO.email}</a></li>
                <li><a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{CONTACT_INFO.phone}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
                <li><Link to="/cgu" className="hover:text-white transition-colors">CGU</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} ResaWork - Un outil de l'Agence MSC - Mon Service Com. Tous droits réservés.
            </p>
            <p className="text-sm text-slate-500">
              {CONTACT_INFO.address}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
